<template>
  <AM3Modal
    :is-open="isOpen"
    width="sm"
    :header="formData.oid ? 'Edit contact details' : 'Add contact manually'"
    @close="closeModal"
  >
    <template #body>
      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <AM3InputText
            ref="new-contact-first-name"
            v-model="formData.firstName"
            label="First name"
            disable-autocomplete
          />
          <AM3InputText
            ref="new-contact-last-name"
            v-model="formData.lastName"
            label="Last name"
            disable-autocomplete
          />
        </div>

        <AM3InputText
          ref="contact-form-email-address"
          v-model="formData.emailAddress"
          label="Email address"
          :validate="validateEmail"
          required
          disable-autocomplete
          :disabled="!!formData.oid"
        />

        <AM3InputMobile
          ref="new-contact-mobile-number"
          v-model="formData.mobileNumber"
          label="Mobile number"
          disable-autocomplete
        />

        <AM3InputText
          ref="new-contact-street-address"
          v-model="formData.streetAddress"
          label="Street address"
          disable-autocomplete
        />

        <div class="grid grid-cols-2 gap-4">
          <AM3InputText
            ref="new-contact-state"
            v-model="formData.state"
            label="State"
            disable-autocomplete
          />
          <AM3InputText
            ref="new-contact-city"
            v-model="formData.city"
            label="City"
            disable-autocomplete
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <AM3InputText
            ref="new-contact-postcode"
            v-model="formData.postcode"
            label="Postcode"
            disable-autocomplete
            placeholder="Enter zip/postcode"
          />
          <AM3InputCountry
            ref="new-contact-country"
            v-model="formData.country"
            label="Country"
            value-type="iso"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <AM3Selectbox
            ref="new-contact-gender"
            v-model="formData.gender"
            label="Gender"
            placeholder="Select a gender"
            data-test-id="gender"
            :options="[{ label: 'Male', value: 'Male' }, { label: 'Female', value: 'Female' }, { label: 'Other', value: 'Other' }]"
          />

          <AM3InputDate
            ref="new-contact-dob"
            v-model="formData.dob"
            label="Date of birth"
            :max-date="maxDob"
            :min-date="'1930-1-1'"
            :default-year="new Date().getFullYear() - 30"
            data-test-id="date-of-birth"
          />
        </div>

        <div
          v-if="!formData.oid"
          class="my-2 border-t border-solid border-weak py-4 space-y-4"
        >
          <MessageListSelector
            v-model="messageListSettings"
            label="Subscribe contact to list"
            :show-recipient-count="true"
          />

          <AM3InputTags
            v-model="selectedTags"
            label="Tag contact"
            placeholder="Search or select tags"
            :options="tags"
            :on-create-tag="createTagCallback"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2">
        <AM3Button
          text="Cancel"
          type="tertiary"
          @click="closeModal"
        />
        <AM3Button
          v-submit="'contact-form'"
          :text="formData.oid ? 'Save Contact' : 'Add Contact'"
          type="primary"
          :loading="isSubmitting"
          data-test-id="save-contact"
          @click="handleSubmit"
        />
      </div>
    </template>
  </AM3Modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import MessageListSelector from '@/components/am3/complex-inputs/MessageListSelector.vue'
import AM3InputMobile from '@/components/am3-generic/inputs/advanced/AM3InputMobile.vue'
import AM3InputCountry from '@/components/am3-generic/inputs/advanced/AM3InputCountry.vue'
import { vSubmit } from '@/components/am3-generic/inputs/AM3FormValidation.js'

export default {
  name: 'ContactFormModal',

  components: {
    MessageListSelector,
    AM3InputMobile,
    AM3InputCountry,
  },
  directives: {
    vSubmit,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    contactData: {
      type: Object,
      default: null,
    },
  },
  emits: ['cancel', 'edit'],
  data() {
    return {
      isSubmitting: false,
      originalEmailAddress: '',
      formData: {
        oid: null,
        firstName: '',
        lastName: '',
        emailAddress: '',
        mobileNumber: '',
        streetAddress: '',
        state: '',
        city: '',
        postcode: '',
        country: null,
        gender: null,
        dob: '',
        contactList: null,
      },
      // AM3InputTags v-models an array of tag oids, not tag objects.
      selectedTags: [],
      // oid -> name cache. FETCH_TAGS replaces the store page on every search,
      // and ADD_TAG never commits to the store, so neither is enough on its own
      // to resolve a selected oid back to the name the API expects.
      tagsByOid: {},
      // Tag names the contact already has (edit mode hides the tag input).
      existingTagNames: [],
      messageListSettings: {
        oid: null,
        smsCanBeEnabled: false,
        emailCanBeEnabled: false,
        smsEnabled: false,
        emailEnabled: false,
        listContainsSms: false,
        listContainsEmail: false,
      },
      messageLists: [],
    }
  },
  computed: {
    ...mapState({
      // Copy before reversing — `.reverse()` mutates the Vuex state array.
      tags: state => [...state.tagModule.tags].reverse(),
    }),

    // The API takes tag names; the input gives us oids.
    selectedTagNames() {
      return this.selectedTags
        .map(oid => this.tagsByOid[oid])
        .filter(Boolean)
    },

    maxDob() {
      const d = new Date()
      d.setFullYear(d.getFullYear() - 1)
      d.setDate(d.getDate() - 1)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0') // months are 0-indexed
      const dd = String(d.getDate()).padStart(2, '0')
      const formatted = `${yyyy}-${mm}-${dd}`
      return formatted
    },
  },

  watch: {
    tags: {
      handler(newTags) {
        for (const tag of newTags) {
          if (tag?.oid != null) {
            this.tagsByOid[tag.oid] = tag.name
          }
        }
      },
      immediate: true,
    },
    isOpen: {
      handler(newValue) {
        if (!newValue) {
          // Reset form when modal is closed
          this.resetForm()
        }
      },
    },
    contactData: {
      handler(newValue) {
        if (newValue) {
          // Store the original email address for comparison
          this.originalEmailAddress = newValue.emailAddress || ''

          // Populate form data with contact data
          this.formData = {
            oid: newValue.oid || null,
            firstName: newValue.firstName || '',
            lastName: newValue.lastName || '',
            emailAddress: newValue.emailAddress || '',
            mobileNumber: newValue.mobileNumber || '',
            // For address fields, preserve empty strings
            streetAddress: newValue.streetAddress,
            state: newValue.state,
            city: newValue.city,
            postcode: newValue.postcode,
            country: newValue.country,
            gender: newValue.gender || null,
            dob: newValue.dob || '',
            contactList: null,
          }

          // Log the address fields for debugging
          console.log('Contact data address fields:', {
            streetAddress: newValue.streetAddress,
            city: newValue.city,
            state: newValue.state,
            postcode: newValue.postcode,
            country: newValue.country,
          })

          // The tag input is hidden in edit mode, so carry the contact's
          // existing tag names through rather than dropping them on update.
          if (Array.isArray(newValue.tags)) {
            this.existingTagNames = newValue.tags
              .map(tag => typeof tag === 'string' ? tag : tag?.name)
              .filter(Boolean)
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions([
      'tagModule/FETCH_TAGS',
      'tagModule/ADD_TAG',
      'audienceModule/CREATE_CONTACT',
      'audienceModule/UPDATE_CONTACT',
    ]),

    resetForm() {
      this.isSubmitting = false
      this.originalEmailAddress = ''
      this.formData = {
        oid: null,
        firstName: '',
        lastName: '',
        emailAddress: '',
        mobileNumber: '',
        streetAddress: '',
        state: '',
        city: '',
        postcode: '',
        country: null,
        gender: null,
        dob: '',
        contactList: null,
      }
      this.selectedTags = []
      this.existingTagNames = []
      this.messageListSettings = {
        oid: null,
        smsCanBeEnabled: false,
        emailCanBeEnabled: false,
        smsEnabled: false,
        emailEnabled: false,
        listContainsSms: false,
        listContainsEmail: false,
      }
    },

    async handleSubmit() {
      this.isSubmitting = true

      // Create a copy of the form data, ensuring all fields are properly formatted
      const formattedData = {
        ...this.formData,
      }

      if (this.formData.oid) {
        // Edit existing contact
        try {
          // Check if email address has changed from its fetched state
          if (formattedData.emailAddress !== this.originalEmailAddress) {
            console.error('Email address has changed from its fetched state:', {
              original: this.originalEmailAddress,
              current: formattedData.emailAddress,
            })
            this.isSubmitting = false
            return
          }

          // Use dedicated UPDATE_CONTACT action for editing existing contacts
          await this['audienceModule/UPDATE_CONTACT']({
            payload: {
              fans: [formattedData],
              tags: this.existingTagNames,
              messageList: this.messageListSettings,
            },
            callback: (success) => {
              if (success) {
                this.isSubmitting = false
                this.$emit('edit', { success: true })
                this.$emit('cancel')
              }
              else {
                this.isSubmitting = false
                this.$arNotification?.push({ type: 'error', message: 'Failed to update contact. Please try again.' })
              }
            },
          })
        }
        catch (error) {
          console.error('Error updating contact:', error)
          this.$arNotification?.push({ type: 'error', message: 'Failed to update contact. Please try again.' })
          this.isSubmitting = false
        }
      }
      else {
        // Create new contact
        this['audienceModule/CREATE_CONTACT']({
          payload: {
            fans: [formattedData],
            tags: this.selectedTagNames,
            messageList: this.messageListSettings,
          },
          callback: (success) => {
            if (success) {
              this.isSubmitting = false
              this.resetForm()
              this.$emit('edit', { success: true })
            }
            else {
              this.isSubmitting = false
            }
          },
        })
      }
    },

    validateEmail(email) {
      if (!email) {
        return true
      }
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(email)) {
        return 'Please enter a valid email address'
      }
      return true
    },

    async createTagCallback(tagName) {
      const createdTag = await this['tagModule/ADD_TAG']({ name: tagName })
      if (createdTag?.oid != null) {
        this.tagsByOid[createdTag.oid] = createdTag.name
      }
      return createdTag || null
    },

    closeModal() {
      this.$emit('cancel')
    },
  },
}
</script>
